import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AxiosResponse } from 'axios'
import apiDefinitions from '../../../../core/api/apiDefinitions'
import { checkPermission } from '../../../../core/permissons/CheckPermissons'
import { showError } from '../../../../core/alert/sweetAlertConfig'
import { all_routes } from '../../../router/all_routes'
import CollapseHeader from '../../../../core/common/collapse-header'
import ViewRoomModule from './ViewRoomModule'
import GlassSelect from '../../../../core/common/glassSelect/GlassSelect'

/** DTOs */
interface LocationDTO {
  id: number; name: string; address: string; lat: number; lng: number;
  description?: string; createdBy: string; createdAt: string; updatedBy?: string; updatedAt?: string;
}
interface BuildingDTO {
  id: number; name: string; noOfFloors: number; locationName: string; locationId: number; description?: string | null;
}
interface FloorDTO {
  id: number; name: string; floorNumber: number; buildingId: number; buildingName: string; locationName: string;
  createdBy: string; createdAt: string; updatedBy?: string; updatedAt?: string;
}
interface RoomDTO {
  id: number; name: string; floorId: number; buildingId: number; status: string;
  createdBy: string; createdAt: string; updatedBy?: string; updatedAt?: string;
}
interface KeyDTO {
  id: string; keyId: string; type: string; noOfKeys: number; roomId?: number;
}
interface DoorDTO {
  id: number; name: string; roomId: number;
}
interface GetLocationsResponse { locations: LocationDTO[]; total: number }

/** Status → classes (neutralized palette + contrasts) */
const roomStatusClasses: Record<string, string> = {
  ACTIVE: 'border-success-subtle bg-success-subtle',
  INACTIVE: 'border-warning-subtle bg-warning-subtle',
  ONGOING_REPAIR: 'border-danger-subtle bg-danger-subtle',
  OCCUPIED: 'border-info-subtle bg-info-subtle',
  RESERVED: 'border-primary-subtle bg-primary-subtle',
}
const getStatusClass = (status?: string) =>
  roomStatusClasses[status?.toUpperCase() ?? ''] || 'border-light bg-light'

/** Tabs */
const navItems = [
  { label: 'View Rooms', icon: 'ti ti-settings', link: all_routes.viewrooms, permission: 'ROLE_ADD_LOCATION' },
  { label: 'Room Register', icon: 'ti ti-settings', link: all_routes.roomregister, permission: 'ROLE_ADD_LOCATION' },
  { label: 'Add Location', icon: 'ti ti-plus', link: all_routes.location, permission: 'ROLE_ADD_LOCATION' },
  { label: 'View Parking Slots', icon: 'ti ti-settings', link: all_routes.viewparkingslots, permission: 'ROLE_GET_PARKING_SLOT' },
  { label: 'Parking Slot Register', icon: 'ti ti-settings', link: all_routes.parkingslotregister, permission: 'ROLE_GET_PARKING_SLOT' },
]

/** Sort helpers */
const numericFirstSort = (a: string, b: string) => {
  const na = parseInt(a.replace(/\D+/g, ''), 10)
  const nb = parseInt(b.replace(/\D+/g, ''), 10)
  if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return na - nb
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}
const chunk = <T,>(arr: T[], size: number) => {
  const out: T[][] = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

const ViewRoom: React.FC = () => {
  const locationHook = useLocation()
  const navigate = useNavigate()

  const [locations, setLocations] = useState<LocationDTO[]>([])
  const [buildings, setBuildings] = useState<BuildingDTO[]>([])
  const [floors, setFloors] = useState<FloorDTO[]>([])
  const [rooms, setRooms] = useState<RoomDTO[]>([])
  const [keyCounts, setKeyCounts] = useState<Record<number, number>>({})
  const [doorCounts, setDoorCounts] = useState<Record<number, number>>({})

  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null)
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(null)
  const [selectedFloorIds, setSelectedFloorIds] = useState<number[]>([])

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [selectedRoom, setSelectedRoom] = useState<RoomDTO | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [isModalLoading, setIsModalLoading] = useState(false)
  const [openingId, setOpeningId] = useState<number | null>(null)

  /** UI filters */
  const [query, setQuery] = useState('')
  const [statusFilters, setStatusFilters] = useState<string[]>([]) // e.g., ['ACTIVE','OCCUPIED']

  const checkToken = () => {
    const token = localStorage.getItem('token')
    if (!token) {
      showError('Authentication token not found. Please log in.')
      navigate('/login')
      return false
    }
    return token
  }

  /** Initial: locations */
  useEffect(() => {
    const run = async () => {
      if (!checkToken()) return
      if (!checkPermission('ROLE_GET_LOCATIONS')) {
        showError('You do not have permission to view locations.')
        return
      }
      setIsLoading(true); setError(null)
      try {
        const payload = { filterType: '', filterValue: '', date: '' }
        const res: AxiosResponse = await apiDefinitions.getLocations('1', payload)
        if (res.status === 200) {
          const data: GetLocationsResponse = res.data.data
          setLocations(data.locations || [])
          if ((data.locations || []).length === 0) setError('No locations found. Please add a location first.')
        } else {
          showError(res.data?.statusDescription || 'Failed to fetch locations')
        }
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.removeItem('token'); navigate('/login')
        } else showError('Failed to fetch locations. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }
    run()
  }, [])

  /** When location changes → buildings */
  useEffect(() => {
    const run = async () => {
      if (!selectedLocationId) {
        setBuildings([]); setSelectedBuildingId(null); setFloors([]); setRooms([]); setSelectedFloorIds([])
        setKeyCounts({}); setDoorCounts({}); return
      }
      if (!checkToken()) return
      if (!checkPermission('ROLE_GET_LOCATIONS')) {
        showError('You do not have permission to view buildings.')
        return
      }
      setIsLoading(true); setError(null)
      try {
        const res: AxiosResponse = await apiDefinitions.getBuildingsByLocationId(selectedLocationId)
        if (res.status === 200) {
          const data: BuildingDTO[] = res.data.data
          setBuildings(data || [])
          setSelectedBuildingId(null)
          setFloors([]); setRooms([]); setSelectedFloorIds([])
          setKeyCounts({}); setDoorCounts({})
          if ((data || []).length === 0) setError(`No buildings found for location ID ${selectedLocationId}.`)
        } else {
          showError(res.data?.statusDescription || 'Failed to fetch buildings')
        }
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.removeItem('token'); navigate('/login')
        } else showError('Failed to fetch buildings. Please try again.')
      } finally {
        setIsLoading(false)
      }
    }
    run()
  }, [selectedLocationId])

  /** When building changes → floors + rooms (per floor) + keys + doors */
  useEffect(() => {
    const run = async () => {
      if (!selectedBuildingId) {
        setFloors([]); setRooms([]); setSelectedFloorIds([]); setKeyCounts({}); setDoorCounts({})
        return
      }
      if (!checkToken()) return

      setIsLoading(true); setError(null)
      try {
        if (!checkPermission('ROLE_VIEW_FLOOR') || !checkPermission('ROLE_GET_ROOMS')) {
          showError('You do not have permission to view floors/rooms.')
          setIsLoading(false); return
        }

        // 1) Fetch floors for building
        const floorsRes: AxiosResponse = await apiDefinitions.getFloorsByBuildingId(selectedBuildingId)
        if (floorsRes.status !== 200) {
          showError(floorsRes.data?.statusDescription || 'Failed to fetch floors')
          setFloors([]); setRooms([]); setSelectedFloorIds([]); setKeyCounts({}); setDoorCounts({})
          return
        }

        const fData: FloorDTO[] = floorsRes.data.data || []
        const sortedFloors = fData.sort((a, b) => {
          if (a.floorNumber === 0) return -1
          if (b.floorNumber === 0) return 1
          return a.floorNumber - b.floorNumber
        })

        setFloors(sortedFloors)
        setSelectedFloorIds(sortedFloors.map(f => f.id))
        if (sortedFloors.length === 0) {
          setRooms([])
          setKeyCounts({})
          setDoorCounts({})
          setError(`No floors found for building ID ${selectedBuildingId}.`)
          return
        }

        // 2) Fetch rooms PER FLOOR (replacing old getRooms by buildingId)
        const roomResponses = await Promise.all(
          sortedFloors.map(f => apiDefinitions.getRoomsByFloorId(f.id))
        )

        let allRooms: RoomDTO[] = []
        roomResponses.forEach((res, idx) => {
          if (res.status === 200 && Array.isArray(res.data.data)) {
            const roomsForFloor: RoomDTO[] = res.data.data.map((r: RoomDTO) => ({
              ...r,
              // Ensure floorId & buildingId are present/consistent
              floorId: sortedFloors[idx].id,
              buildingId: selectedBuildingId,
            }))
            allRooms = allRooms.concat(roomsForFloor)
          }
        })

        const sortedRooms = allRooms.slice().sort((a, b) => numericFirstSort(a.name, b.name))
        setRooms(sortedRooms)

        if (allRooms.length === 0) {
          setError(`No rooms found for building ID ${selectedBuildingId}.`)
          setKeyCounts({})
          setDoorCounts({})
          return
        }

        // 3) Keys per room (fetch doors, then keys under each door)
        const keyPromises = sortedRooms.map(async (room) => {
          try {
            const doorsRes = await apiDefinitions.getDoorsByRoomId(room.id)
            if (doorsRes.status === 200 && Array.isArray(doorsRes.data.data)) {
              const doors: DoorDTO[] = doorsRes.data.data
              let totalKeys = 0

              for (const door of doors) {
                const keysRes = await apiDefinitions.getKeysByDoorId(door.id)
                if (keysRes.status === 200 && keysRes.data.status === 'S1000') {
                  const keys: KeyDTO[] = keysRes.data.data || []
                  totalKeys += keys.reduce((sum, k) => sum + (k.noOfKeys || 0), 0)
                }
              }
              return { roomId: room.id, total: totalKeys, doors: doors.length }
            }
          } catch (e: any) {
            // Silent fail → 0 keys/doors
          }
          return { roomId: room.id, total: 0, doors: 0 }
        })

        const keyResults = await Promise.all(keyPromises)

        const keyCountMap = keyResults.reduce<Record<number, number>>((acc, r) => {
          acc[r.roomId] = r.total
          return acc
        }, {})
        setKeyCounts(keyCountMap)

        const doorCountMap = keyResults.reduce<Record<number, number>>((acc, r) => {
          acc[r.roomId] = r.doors
          return acc
        }, {})
        setDoorCounts(doorCountMap)
      } catch (e: any) {
        if (e.response?.status === 401) {
          localStorage.removeItem('token'); navigate('/login')
        } else {
          showError('Failed to fetch floors/rooms. Please try again.')
        }
      } finally {
        setIsLoading(false)
      }
    }
    run()
  }, [selectedBuildingId])

  const selectedLocationName = useMemo(
    () => (selectedLocationId ? (locations.find(l => l.id === selectedLocationId)?.name || '') : ''),
    [selectedLocationId, locations]
  )
  const selectedBuildingName = useMemo(
    () => (selectedBuildingId ? (buildings.find(b => b.id === selectedBuildingId)?.name || '') : ''),
    [selectedBuildingId, buildings]
  )

  const openRoom = async (roomId: number) => {
    if (openingId === roomId) return
    setOpeningId(roomId)

    if (!checkToken()) { setOpeningId(null); return }
    if (!checkPermission('ROLE_GET_ROOMS')) {
      showError('You do not have permission to view room details.')
      setOpeningId(null); return
    }

    setIsModalLoading(true)
    setSelectedRoom(null)
    try {
      const res: AxiosResponse = await apiDefinitions.getRoomById(roomId)
      if (res.status === 200 && res.data?.data?.id) setSelectedRoom(res.data.data as RoomDTO)
      else showError(res.data?.statusDescription || 'Failed to fetch room details')
      setShowModal(true)
    } catch {
      showError('Failed to fetch room details. Please try again.')
    } finally {
      setIsModalLoading(false)
      setOpeningId(null)
    }
  }

  /** Derived: filtered rooms by search + status + selected floors */
  const visibleRoomsByFloor = useMemo(() => {
    const q = query.trim().toLowerCase()
    const statusSet = new Set(statusFilters.map(s => s.toUpperCase()))
    const byFloor: Record<number, RoomDTO[]> = {}

    const activeFloors = new Set(selectedFloorIds)
    rooms.forEach(r => {
      if (!activeFloors.has(r.floorId)) return
      if (q && !r.name.toLowerCase().includes(q)) return
      if (statusSet.size && !statusSet.has((r.status || '').toUpperCase())) return
      if (!byFloor[r.floorId]) byFloor[r.floorId] = []
      byFloor[r.floorId].push(r)
    })
    Object.keys(byFloor).forEach(fid => {
      byFloor[+fid] = byFloor[+fid].sort((a, b) => numericFirstSort(a.name, b.name))
    })
    return byFloor
  }, [rooms, selectedFloorIds, query, statusFilters])

  /** UI helpers */
  const RoomCard: React.FC<{ room: RoomDTO }> = ({ room }) => (
    <div
      className={`card h-100 cursor-pointer border border-2 room-card ${getStatusClass(room.status)}`}
      title={room.name}
      onClick={() => openRoom(room.id)}
    >
      <div className="card-body py-1 px-2 d-flex justify-content-between align-items-center">
        <span className="fw-semibold text-dark room-card__label">{room.name}</span>
        <div className="d-flex gap-1 align-items-center">
          <span className="badge bg-dark-subtle text-dark rounded-pill" title={`${keyCounts[room.id] || 0} key(s)`}>
            <i className="ti ti-key me-1"></i>{keyCounts[room.id] || 0}
          </span>
          <span className="badge bg-secondary-subtle text-dark rounded-pill" title={`${doorCounts[room.id] || 0} door(s)`}>
            <i className="ti ti-door me-1"></i>{doorCounts[room.id] || 0}
          </span>
        </div>
      </div>
    </div>
  )

  const toggleStatus = (value: string) => {
    setStatusFilters(prev => prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value])
  }

  const STATUSES = [
    { key: 'ACTIVE', label: 'Active' },
    { key: 'OCCUPIED', label: 'Occupied' },
    { key: 'RESERVED', label: 'Reserved' },
    { key: 'ONGOING_REPAIR', label: 'Repair' },
    { key: 'INACTIVE', label: 'Inactive' },
  ]

  return (
    <div className="page-wrapper">
      {/* Scoped CSS */}
      <style>{`
        .room-card { min-height: 46px; border-radius: .6rem; transition: transform .12s ease, box-shadow .12s ease; }
        .room-card:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(0,0,0,.08); }
        .room-card .card-body { line-height: 1; }
        .room-card__label { font-size: .92rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 62%; display: inline-block; }
        .room-card .badge { font-size: .78rem; padding: .25rem .5rem; }
        .floor-strip { border-radius: .75rem; border: 1px solid var(--bs-border-color); background: var(--bs-body-bg); }
        .status-chip { cursor: pointer; user-select: none; transition: transform .08s ease; }
        .status-chip:active { transform: scale(.98); }
        .legend-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; margin-right: .35rem; }
        .sticky-actions { position: sticky; top: .5rem; z-index: 1; }
        @media (min-width: 768px) {
          .room-card { min-height: 44px; }
          .room-card__label { font-size: .88rem; }
          .room-card .badge { font-size: .75rem; }
        }
      `}</style>

      <div className="content">
        {/* Header + tabs */}
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Administration/Location/Room View</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons"><CollapseHeader /></div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul className="nav nav-tabs nav-tabs-bottom" style={{ gap: '12px', display: 'flex' }}>
                  {navItems.map((item, index) =>
                    item.permission && checkPermission(item.permission) ? (
                      <li className="nav-item me-item" key={index}>
                        <Link to={item.link} className={`nav-link px-0 ${index === 0 ? 'active' : ''}`}>
                          <i className={item.icon}></i> {item.label}
                        </Link>
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Filters & toolbar */}
        <div className="card">
          <div className="card-header justify-content-between">
            <div className="card-title">View Rooms</div>
          </div>

          <div className="card-body">
            <div className="row g-2 align-items-end">
              {/* Location */}
              <div className="col-12 col-md-4 col-lg-3">
                <label className="form-label">Select Location</label>
                <GlassSelect
                  options={locations.map(loc => ({ value: loc.id, label: loc.name }))}
                  value={selectedLocationId}
                  onChange={(val) => setSelectedLocationId(val ? Number(val) : null)}
                  placeholder={locations.length === 0 ? 'No locations available' : 'Select a location'}
                  disabled={isLoading}
                  searchable
                  allowClear
                />
              </div>

              {/* Building */}
              <div className="col-12 col-md-4 col-lg-3">
                <label className="form-label">Select Building</label>
                <GlassSelect
                  options={buildings.map(b => ({ value: b.id, label: b.name }))}
                  value={selectedBuildingId}
                  onChange={(val) => setSelectedBuildingId(val ? Number(val) : null)}
                  placeholder={buildings.length === 0 ? 'No buildings available' : 'Select a building'}
                  disabled={isLoading || !selectedLocationId}
                  searchable
                  allowClear
                />
              </div>

              {/* Floors (multi-checkbox) */}
              <div className="col-12 col-md-4 col-lg-6">
                <label className="form-label">Select Floors</label>
                <div className="d-flex flex-wrap gap-2">
                  {floors.map(f => (
                    <div key={f.id} className="form-check me-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`floor-${f.id}`}
                        checked={selectedFloorIds.includes(f.id)}
                        onChange={() =>
                          setSelectedFloorIds(prev => prev.includes(f.id) ? prev.filter(x => x !== f.id) : [...prev, f.id])
                        }
                        disabled={isLoading || !selectedBuildingId}
                      />
                      <label className="form-check-label" htmlFor={`floor-${f.id}`}>{f.name}</label>
                    </div>
                  ))}
                  {floors.length === 0 && <span className="text-muted">No floors</span>}
                </div>
              </div>
            </div>

            {/* Search + Status chips */}
            <div className="row g-2 mt-2 align-items-end">
              <div className="col-12 col-md-5 col-lg-4">
                <label className="form-label">Search rooms</label>
                <input
                  className="form-control"
                  type="text"
                  placeholder="Search by room name…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <div className="col-12 col-md-7 col-lg-8">
                <label className="form-label">Status</label>
                <div className="d-flex flex-wrap gap-2">
                  {STATUSES.map(s => {
                    const active = statusFilters.includes(s.key)
                    return (
                      <span
                        key={s.key}
                        className={`badge rounded-pill px-3 py-2 status-chip ${active ? 'bg-primary text-white' : 'bg-light text-dark border'}`}
                        role="button"
                        onClick={() => toggleStatus(s.key)}
                        title={s.label}
                      >
                        {s.label}
                      </span>
                    )
                  })}
                  {statusFilters.length > 0 && (
                    <button className="btn btn-sm btn-outline-secondary ms-1" onClick={() => setStatusFilters([])}>
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Legend + quick counters */}
            <div className="row mt-3 g-2">
              <div className="col-12 col-md-8">
                <div className="d-flex flex-wrap gap-3 align-items-center">
                  <span className="text-muted small">Legend:</span>
                  <span className="small"><span className="legend-dot bg-success-subtle border border-success-subtle"></span>Active</span>
                  <span className="small"><span className="legend-dot bg-info-subtle border border-info-subtle"></span>Occupied</span>
                  <span className="small"><span className="legend-dot bg-primary-subtle border border-primary-subtle"></span>Reserved</span>
                  <span className="small"><span className="legend-dot bg-danger-subtle border border-danger-subtle"></span>Repair</span>
                  <span className="small"><span className="legend-dot bg-warning-subtle border border-warning-subtle"></span>Inactive</span>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <div className="d-flex gap-2 justify-content-md-end">
                  <span className="badge bg-light text-dark border">Floors: {selectedFloorIds.length}</span>
                  <span className="badge bg-light text-dark border">Rooms: {rooms.length}</span>
                </div>
              </div>
            </div>

            {isLoading && (
              <div className="mt-3">
                <div className="placeholder-glow">
                  <div className="placeholder col-12 mb-2" style={{ height: 14 }}></div>
                  <div className="placeholder col-10 mb-2" style={{ height: 14 }}></div>
                  <div className="placeholder col-8" style={{ height: 14 }}></div>
                </div>
              </div>
            )}

            {error && <div className="alert alert-danger mt-3" role="alert">{error}</div>}
          </div>
        </div>

        {/* Content */}
        <div className="card">
          <div className="card-body">
            {selectedLocationId && selectedBuildingId && floors.length > 0 ? (
              <div className="mb-3">
                <h5 className="text-center mb-4">
                  {selectedLocationName} — <span className="fw-semibold">🏢 {selectedBuildingName}</span>
                </h5>

                <div className="d-flex flex-column gap-3">
                  {floors.map(floor => {
                    if (!selectedFloorIds.includes(floor.id)) return null
                    const floorRooms = visibleRoomsByFloor[floor.id] || []
                    const rows = chunk(floorRooms, 12)
                    return (
                      <div key={floor.id} className="floor-strip p-3">
                        <div className="d-flex align-items-start">
                          <div className="me-3 d-none d-md-flex align-items-center sticky-actions">
                            <span
                              className="badge bg-primary rounded-3"
                              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: '1px' }}
                              title={floor.name}
                            >
                              {floor.name}
                            </span>
                          </div>

                          <div className="flex-grow-1 w-100">
                            {floorRooms.length === 0 ? (
                              <div className="alert alert-light border d-flex align-items-center mb-0" role="alert">
                                <i className="ti ti-info-circle me-2"></i>
                                No rooms match current filters on this floor.
                              </div>
                            ) : (
                              rows.map((rset, idx) => (
                                <div key={idx} className={`row g-2 ${idx > 0 ? 'mt-1' : ''} align-items-stretch`}>
                                  {rset.map(r => (
                                    <div key={r.id} className="col-6 col-sm-3 col-md-2">
                                      <RoomCard room={r} />
                                    </div>
                                  ))}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : (
              <div className="alert alert-light border d-flex align-items-center" role="alert">
                <i className="ti ti-info-circle me-2"></i>
                Select a <strong className="mx-1">Location</strong>, a <strong className="mx-1">Building</strong>, and at least one <strong className="mx-1">Floor</strong> to view rooms.
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        <ViewRoomModule
          show={showModal}
          onHide={() => { setShowModal(false); setSelectedRoom(null) }}
          room={isModalLoading ? null : selectedRoom}
        />
      </div>
    </div>
  )
}

export default ViewRoom
